import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import * as api from "../lib/api";
import { getRankIcon, isErRank } from "../lib/ranks";
import "./KabileDetail.css";

interface KabileMember {
  id: string;
  username: string;
  avatarUrl?: string;
  savwarTP: number;
  savwarRank: number;
  earnedKP: number;
  joinedAt: Date;
  role: 'leader' | 'member';
}

interface KabileData {
  id: string;
  name: string;
  description: string;
  flagUrl?: string;
  leaderId: string;
  founderId?: string;
  maxMembers: number;
  minTP: number;
  createdAt: Date;
  members: KabileMember[];
  totalTP: number;
  totalKP: number;
  isUserInKabile: boolean;
  hasPendingRequest: boolean;
}

export default function KabileDetail() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { kabileId } = useParams<{ kabileId: string }>();
  const [kabile, setKabile] = useState<KabileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequestingJoin, setIsRequestingJoin] = useState(false);
  const [joinError, setJoinError] = useState<string>("");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedMemberForTransfer, setSelectedMemberForTransfer] = useState<string | null>(null);

  useEffect(() => {
    if (kabileId) {
      loadKabile();
    }
  }, [kabileId]);

  const loadKabile = async () => {
    if (!kabileId) return;
    
    try {
      const data = await api.getKabileById(kabileId);
      
      // Convert relative URLs to absolute for local development
      if (data && data.flagUrl && data.flagUrl.startsWith('/uploads/')) {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:5003';
        data.flagUrl = apiBase + data.flagUrl;
      }
      
      setKabile(data);
    } catch (error) {
      console.error("Kabile yüklenemedi:", error);
      setKabile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRequest = async () => {
    if (!kabile || !kabileId || !user) return;
    
    setJoinError("");
    setIsRequestingJoin(true);
    
    try {
      await api.requestJoinKabile(kabileId);
      
      // Update state to show pending request
      setKabile({ ...kabile, hasPendingRequest: true });
    } catch (error: any) {
      setJoinError(error.message || "Katılma talebi gönderilirken bir hata oluştu");
    } finally {
      setIsRequestingJoin(false);
    }
  };

  const handleLeaveKabile = async () => {
    if (!kabile || !user) return;
    
    const confirmed = window.confirm(
      "Kabileden ayrılmak istediğinize emin misiniz? Kazandığınız KP puanları kabilede kalacaktır."
    );
    
    if (!confirmed) return;
    
    try {
      await api.leaveKabile();
      alert("Kabileden başarıyla ayrıldınız");
      navigate("/savkabile");
    } catch (error: any) {
      alert(error.message || "Kabileden ayrılırken bir hata oluştu");
    }
  };

  const handleTransferFounder = async () => {
    if (!selectedMemberForTransfer || !kabileId) return;
    
    const confirmed = window.confirm(
      "Kurucu rolünü devretmek istediğinize emin misiniz? Bu işlem geri alınamaz."
    );
    
    if (!confirmed) return;
    
    try {
      await api.transferFounder(kabileId, selectedMemberForTransfer);
      alert("Kurucu rolü başarıyla devredildi");
      setShowTransferModal(false);
      loadKabile(); // Reload to show new founder
    } catch (error: any) {
      alert(error.message || "Kurucu devredilirken bir hata oluştu");
    }
  };

  const canJoinKabile = (): { canJoin: boolean; reason: string } => {
    if (!kabile || !user) return { canJoin: false, reason: "Bilgi yüklenemedi" };
    
    if (kabile.isUserInKabile) {
      return { canJoin: false, reason: "Zaten bu kabiledesiniz" };
    }
    
    if (kabile.hasPendingRequest) {
      return { canJoin: false, reason: "Bekleyen talebiniz var" };
    }
    
    if (kabile.members.length >= kabile.maxMembers) {
      return { canJoin: false, reason: "Kabile dolu" };
    }
    
    const userTP = user.savwarTP || 0;
    if (userTP < kabile.minTP) {
      return { canJoin: false, reason: `${kabile.minTP} TP gerekli (Mevcut: ${userTP})` };
    }
    
    return { canJoin: true, reason: "" };
  };

  if (isLoading) {
    return (
      <div className="kabile-detail-container">
        <div className="kabile-loading">
          <div className="loading-spinner"></div>
          <p>Kabile bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!kabile) {
    return (
      <div className="kabile-detail-container">
        <div className="no-kabile">
          <h2>🏛️ Kabile Bulunamadı</h2>
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Kabileye Dön
          </button>
        </div>
      </div>
    );
  }

  const memberCount = kabile.members.length;
  const memberPercentage = (memberCount / kabile.maxMembers) * 100;
  const joinStatus = canJoinKabile();

  return (
    <div className="kabile-detail-container">
      <div className="kabile-detail-content">
        {/* Header */}
        <header className="kabile-header">
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Geri
          </button>
          
          <div className="kabile-title-section">
            {kabile.flagUrl ? (
              <img 
                src={kabile.flagUrl} 
                alt="Kabile Bayrağı" 
                className="kabile-flag"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="kabile-flag-placeholder">🏛️</div>
            )}
            <div>
              <h1 className="kabile-name">{kabile.name}</h1>
              <p className="kabile-founded-date">📅 {new Date(kabile.createdAt).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })} tarihinde kuruldu</p>
            </div>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="kabile-stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <div className="stat-label">Üye Sayısı</div>
              <div className="stat-value">{memberCount} / {kabile.maxMembers}</div>
              <div className="stat-progress">
                <div className="stat-progress-bar" style={{ width: `${memberPercentage}%` }}></div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⚔️</div>
            <div className="stat-info">
              <div className="stat-label">Toplam TP</div>
              <div className="stat-value">{kabile.totalTP.toLocaleString()}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-info">
              <div className="stat-label">Min TP Şartı</div>
              <div className="stat-value">{kabile.minTP.toLocaleString()}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <div className="stat-label">Toplam KP</div>
              <div className="stat-value">{kabile.totalKP.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Description */}
        {kabile.description && (
          <div className="kabile-description-card">
            <h3>📜 Hakkında</h3>
            <p>{kabile.description}</p>
          </div>
        )}

        {/* Join Button Section */}
        {!kabile.isUserInKabile && (
          <div className="join-section">
            {joinError && (
              <div className="error-message">{joinError}</div>
            )}
            
            <button
              onClick={handleJoinRequest}
              disabled={!joinStatus.canJoin || isRequestingJoin}
              className={`join-button ${!joinStatus.canJoin ? 'disabled' : ''}`}
              title={!joinStatus.canJoin ? joinStatus.reason : 'Kabileye katılma talebi gönder'}
            >
              {isRequestingJoin ? '⏳ Gönderiliyor...' : 
               kabile.hasPendingRequest ? '⏳ Talep Beklemede' :
               '✉️ Katılma Talebi Gönder'}
            </button>
            
            {!joinStatus.canJoin && (
              <div className="join-warning">
                ⚠️ {joinStatus.reason}
              </div>
            )}
          </div>
        )}

        {kabile.isUserInKabile && (
          <div className="member-badge">
            ✅ Bu kabileye üyesiniz
            
            <div className="member-actions">
              <button onClick={handleLeaveKabile} className="leave-button">
                🚪 Kabileden Ayrıl
              </button>
              
              {user && kabile.founderId === user.id && (
                <button 
                  onClick={() => setShowTransferModal(true)} 
                  className="transfer-button"
                >
                  👑 Kurucu Rolünü Devret
                </button>
              )}
            </div>
          </div>
        )}

        {/* Leader Info Section */}
        <div className="kabile-leaders-section">
          <div className="section-header">
            <h3>👑 Liderlik</h3>
          </div>

          <div className="leaders-grid">
            {/* Founder */}
            {kabile.founderId && (() => {
              const founder = kabile.members.find(m => m.id === kabile.founderId);
              return founder ? (
                <div className="leader-card founder-card">
                  <div className="leader-badge">🏛️ Kurucu Lider</div>
                  <div className="leader-info">
                    {founder.avatarUrl ? (
                      <img src={founder.avatarUrl} alt={founder.username} className="leader-avatar" />
                    ) : (
                      <div className="leader-avatar-placeholder">
                        {founder.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="leader-details">
                      <Link to={`/profile/${founder.id}`} className="leader-name leader-name-link">{founder.username}</Link>
                      <div className="leader-stats">
                        <span>⚔️ {founder.savwarTP.toLocaleString()} TP</span>
                        {isErRank(founder.savwarRank) ? (
                          <span style={{ fontSize: 20 }}>🟢</span>
                        ) : (
                          <img 
                            src={getRankIcon(founder.savwarRank)} 
                            alt="rank" 
                            className="rank-icon"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Current Leader (if different from founder) */}
            {(() => {
              const currentLeader = kabile.members.find(m => m.id === kabile.leaderId);
              const isFounderLeader = kabile.founderId === kabile.leaderId;
              
              return currentLeader && !isFounderLeader ? (
                <div className="leader-card current-leader-card">
                  <div className="leader-badge">👑 Mevcut Lider</div>
                  <div className="leader-info">
                    {currentLeader.avatarUrl ? (
                      <img src={currentLeader.avatarUrl} alt={currentLeader.username} className="leader-avatar" />
                    ) : (
                      <div className="leader-avatar-placeholder">
                        {currentLeader.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="leader-details">
                      <Link to={`/profile/${currentLeader.id}`} className="leader-name leader-name-link">{currentLeader.username}</Link>
                      <div className="leader-stats">
                        <span>⚔️ {currentLeader.savwarTP.toLocaleString()} TP</span>
                        {isErRank(currentLeader.savwarRank) ? (
                          <span style={{ fontSize: 20 }}>🟢</span>
                        ) : (
                          <img 
                            src={getRankIcon(currentLeader.savwarRank)} 
                            alt="rank" 
                            className="rank-icon"
                          />
                        )}
                      </div>
                      <div className="leader-note">En yüksek TP</div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {kabile.founderId === kabile.leaderId && (
            <div className="leadership-note">
              ℹ️ Kurucu lider aynı zamanda en yüksek TP'ye sahip olduğu için halen lider.
            </div>
          )}
        </div>

        {/* Members List */}
        <div className="kabile-members-section">
          <div className="section-header">
            <h3>👥 Üyeler ({memberCount})</h3>
          </div>

          <div className="members-list">
            {kabile.members.map(member => (
              <div key={member.id} className="member-card">
                <div className="member-info">
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt={member.username} className="member-avatar" />
                  ) : (
                    <div className="member-avatar-placeholder">
                      {member.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="member-details">
                    <div className="member-name">
                      <Link to={`/profile/${member.id}`} className="member-name-link">{member.username}</Link>
                      {member.id === kabile.founderId && <span className="founder-badge-mini">🏛️</span>}
                      {member.id === kabile.leaderId && <span className="leader-crown">👑</span>}
                    </div>
                    <div className="member-stats">
                      <span>⚔️ {member.savwarTP.toLocaleString()} TP</span>
                      <span className="member-kp">🏆 {member.earnedKP.toLocaleString()} KP</span>
                      {isErRank(member.savwarRank) ? (
                        <span style={{ fontSize: 20 }}>🟢</span>
                      ) : (
                        <img 
                          src={getRankIcon(member.savwarRank)} 
                          alt="rank" 
                          className="rank-icon"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transfer Founder Modal */}
        {showTransferModal && (
          <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>👑 Kurucu Rolünü Devret</h2>
              <p>Kurucu rolünü hangi üyeye devretmek istersiniz?</p>
              
              <div className="transfer-member-list">
                {kabile.members
                  .filter(m => m.id !== user?.id) // Don't show current user
                  .map(member => (
                    <div 
                      key={member.id}
                      className={`transfer-member-item ${selectedMemberForTransfer === member.id ? 'selected' : ''}`}
                      onClick={() => setSelectedMemberForTransfer(member.id)}
                    >
                      {member.avatarUrl ? (
                        <img src={member.avatarUrl} alt={member.username} className="transfer-avatar" />
                      ) : (
                        <div className="transfer-avatar-placeholder">
                          {member.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="transfer-member-info">
                        <span className="transfer-username">{member.username}</span>
                        <span className="transfer-tp">⚔️ {member.savwarTP.toLocaleString()} TP</span>
                      </div>
                      {selectedMemberForTransfer === member.id && (
                        <span className="selected-check">✓</span>
                      )}
                    </div>
                  ))}
              </div>

              <div className="modal-actions">
                <button 
                  onClick={handleTransferFounder}
                  disabled={!selectedMemberForTransfer}
                  className="modal-confirm-btn"
                >
                  Devret
                </button>
                <button 
                  onClick={() => {
                    setShowTransferModal(false);
                    setSelectedMemberForTransfer(null);
                  }}
                  className="modal-cancel-btn"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
